import React, { useEffect, useMemo, useState } from 'react';
import { renderLinkifiedText } from '../../utils/linkify';
import { useNavigate } from 'react-router-dom';
import { deleteField, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useJoin } from './joinContext';
import ErrorMessage from '../forms/formComponents/ErrorMessage';
import { getFunctionUrl } from '../../utils/functionURLs';
import LoadingModal from '../ui/LoadingModal';
import { getCurrencyMeta } from '../../utils/currency';
import { db, functions } from '../../firebase-client';
import { getMembershipHandling, isClubManagedMembershipMode, MEMBERSHIP_HANDLING_MODES } from '../../utils/membershipHandling';
import { formatCadenceOptionLabel } from '../../utils/cadenceAdjustments';
const PRICING_QUESTION_IDS = ['pricingTags', 'pricingTag'];
const isPricingQuestion = q => PRICING_QUESTION_IDS.includes(q?.id);
function formatApprovalTime(averageMs) {
  if (!Number.isFinite(averageMs) || averageMs < 0) return null;
  if (averageMs < 3600000) return 'under an hour';
  if (averageMs < 48 * 3600000) {
    const hours = Math.round(averageMs / 3600000);
    return `around ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  const days = Math.round(averageMs / 86400000);
  return `around ${days} days`;
}

export default function JoinPayment({ onlineRouteSetup = null }) {
  const {
    user,
    team,
    teamId,
    stripeAmount,
    stripeFeeBreakdown,
    setStatus,
    stripeSetup,
    cadenceMode,
    // 'fixed' | 'member_choice'
    cadenceOptions,
    // [{ key:'monthly'|'yearly', label, unit, count, discountPct }]
    selectedCadence,
    setSelectedCadence,
    membershipPlanType,
    selectedFamilyTier,
    selectedFamilyTierId,
    familyMembershipEnabled,
    membershipTargetUserIds,
    membershipTargetCount,
    membershipPricingTag,
    membershipDiscountCode,
    isPrelaunchCommitteeMode: prelaunchCommittee,
    setMembershipDiscountCode,
    selectedFamilyId,
    selectedIndividualTargetFamilyId,
    questions,
    requiredJoinDocuments,
    pendingMemberStatus,
    setPendingMemberStatus,
    setJoinPaymentSummary,
  } = useJoin();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [appliedPricingPreview, setAppliedPricingPreview] = useState(null);
  const [hybridPricingPreview, setHybridPricingPreview] = useState(null);
  const [hybridQuoteError, setHybridQuoteError] = useState(null);
  const [hybridQuoteRetry, setHybridQuoteRetry] = useState(0);
  const [discountMessage, setDiscountMessage] = useState('');
  const membershipHandling = getMembershipHandling(team || {});
  const hybrid = membershipHandling.mode === MEMBERSHIP_HANDLING_MODES.HYBRID;
  const clubManaged = isClubManagedMembershipMode(membershipHandling.mode);
  const [membershipCollectionRoute, setMembershipCollectionRoute] = useState(null);

  // Local choice so nothing is auto-selected in member_choice mode
  const [choice, setChoice] = useState(null);

  // Initialise: no selection for member_choice; auto-pick only option for fixed
  useEffect(() => {
    if (onlineRouteSetup) {
      const cadence = cadenceOptions?.find(option => option.unit === onlineRouteSetup.interval) || selectedCadence;
      setChoice(cadence);
      setMembershipCollectionRoute('stripe');
      if (cadence) setSelectedCadence(cadence);
    } else if (cadenceMode === 'member_choice') {
      setChoice(null);
    } else {
      const fixed = cadenceOptions?.[0] || null;
      setChoice(fixed);
      if (fixed) setSelectedCadence(fixed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadenceMode, Array.isArray(cadenceOptions) ? cadenceOptions.length : 0, onlineRouteSetup]);

  // Sync to context only when user chooses in member_choice
  useEffect(() => {
    if (cadenceMode === 'member_choice') setSelectedCadence(choice || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice, cadenceMode]);
  const canShowPricing = cadenceMode === 'fixed' || !!choice;
  const hasMembershipTarget = Array.isArray(membershipTargetUserIds) && membershipTargetUserIds.length > 0;
  const canPay = !submitting && canShowPricing && hasMembershipTarget;
  const currency = stripeSetup?.currency || 'GBP';
  const formatMinor = minor => {
    const {
      symbol,
      decimals
    } = getCurrencyMeta(currency);
    const major = Number(minor || 0) / Math.pow(10, decimals);
    return `${symbol}${major.toFixed(decimals)}`;
  };
  const normalizedDiscountCode = membershipDiscountCode.trim().toUpperCase();
  const activePreview = appliedPricingPreview?.code === normalizedDiscountCode ? appliedPricingPreview : null;
  const effectiveBreakdown = activePreview?.breakdown || hybridPricingPreview || stripeFeeBreakdown;
  const membershipTargetIdsKey = Array.isArray(membershipTargetUserIds) ? membershipTargetUserIds.join('|') : '';
  const effectiveBaseAmount = Number(effectiveBreakdown?.baseAmount || 0);
  const offlineBaseAmount = hybrid
    ? Number(hybridPricingPreview?.baseAmount || stripeFeeBreakdown?.baseAmount || 0)
    : effectiveBaseAmount;
  const effectivePlatformFee = Number(effectiveBreakdown?.platformFee || 0);
  const effectiveStripeFee = Number(effectiveBreakdown?.stripeFee || 0);
  const effectiveFinalAmount = Number(effectiveBreakdown?.finalAmount || 0);
  const offlineApprovalTime = formatApprovalTime(hybridPricingPreview?.offlineApprovalTime?.averageMs);
  const hybridOnlineDifference = Math.max(0, effectiveFinalAmount - offlineBaseAmount);
  const effectiveDiscountAppliedMinor = Number(activePreview?.membershipDiscountAppliedMinor || 0);
  const effectiveStoredCreditAppliedMinor = Number(activePreview?.membershipStoredCreditAppliedMinor || 0);
  const effectiveStoredCreditRemainingMinor = Number(activePreview?.membershipStoredCreditRemainingMinor || 0);
  const effectiveCadence = cadenceMode === 'member_choice' ? choice : choice || selectedCadence || cadenceOptions?.[0] || null;
  const cadenceSummary = useMemo(() => {
    if (!effectiveCadence) return '';
    return formatCadenceOptionLabel(effectiveCadence.label, effectiveCadence.discountPct);
  }, [effectiveCadence]);
  useEffect(() => {
    setJoinPaymentSummary?.(hybrid ? { route: membershipCollectionRoute, breakdown: canShowPricing ? effectiveBreakdown : null,
      renewalSetup: Boolean(onlineRouteSetup), renewalAt: onlineRouteSetup?.pendingMembershipCollectionRouteChange?.effectiveAt?.toDate?.()?.toISOString() } : null);
  }, [canShowPricing, effectiveBreakdown, hybrid, membershipCollectionRoute, onlineRouteSetup, setJoinPaymentSummary]);
  useEffect(() => {
    if (!hybrid || onlineRouteSetup || !user || !teamId || !canShowPricing || !hasMembershipTarget || !effectiveCadence) {
      setHybridPricingPreview(null);
      return undefined;
    }
    let cancelled = false;
    setHybridPricingPreview(null);
    setHybridQuoteError(null);
    const loadQuote = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(getFunctionUrl('createManualCheckoutSession'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            previewOnly: true,
            teamId,
            userId: user.uid,
            memberId: user.uid,
            paymentType: 'membership',
            membershipPlanType,
            membershipPlanTierId: membershipPlanType === 'family_tier' ? selectedFamilyTier?.id || selectedFamilyTierId || null : null,
            membershipTargetUserIds,
            membershipTargetFamilyId: membershipTargetUserIds?.some(uid => uid && uid !== user.uid)
              ? membershipPlanType === 'individual' ? selectedIndividualTargetFamilyId || null : selectedFamilyId || null
              : null,
            membershipPricingTag: membershipPricingTag || '',
            selectedCadence: {
              unit: effectiveCadence.unit,
              count: effectiveCadence.count,
              discountPct: effectiveCadence.discountPct || 0,
              key: effectiveCadence.key,
            },
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Could not load the payment comparison.');
        if (!cancelled) setHybridPricingPreview({
          offlineApprovalTime: data.offlineApprovalTime || null,
          baseAmount: Number(data.baseAmount || 0),
          platformFee: Number(data.platformFee || 0),
          stripeFee: Number(data.stripeFee || 0),
          finalAmount: Number(data.finalAmount || 0),
        });
      } catch (quoteError) {
        if (!cancelled) setHybridQuoteError(quoteError?.message || 'Could not load the payment comparison.');
      }
    };
    loadQuote();
    return () => { cancelled = true; };
  }, [canShowPricing, effectiveCadence, hasMembershipTarget, hybrid, hybridQuoteRetry, membershipPlanType, membershipPricingTag, membershipTargetIdsKey, membershipTargetUserIds, onlineRouteSetup, selectedFamilyId, selectedFamilyTier?.id, selectedFamilyTierId, selectedIndividualTargetFamilyId, teamId, user]);
  const handlePaymentChoice = async method => {
    if (!canPay) return;
    setSubmitting(true);
    setError(null);
    try {
      if (!user) throw new Error('You must be signed in to pay.');
      if (!hasMembershipTarget) throw new Error('Please choose which account this membership is for.');
      const userId = user.uid;
      const token = await user.getIdToken();
      const endpoint = prelaunchCommittee || method === 'recurring' ? 'createCustomerAndSubscription' : 'createManualCheckoutSession';
      const today = new Date();
      const billingCycle = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const cadencePayload = effectiveCadence ? {
        selectedCadence: {
          unit: effectiveCadence.unit,
          count: effectiveCadence.count,
          discountPct: effectiveCadence.discountPct || 0,
          key: effectiveCadence.key
        }
      } : {};
      const res = await fetch(getFunctionUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          teamId,
          userId,
          memberId: userId,
          paymentType: 'membership',
          billingCycle,
          membershipPlanType,
          membershipPlanTierId: membershipPlanType === 'family_tier' ? selectedFamilyTier?.id || selectedFamilyTierId || null : null,
          membershipTargetUserIds,
          membershipTargetFamilyId: membershipTargetUserIds?.some(uid => uid && uid !== userId) ? membershipPlanType === 'individual' ? selectedIndividualTargetFamilyId || null : selectedFamilyId || null : null,
          membershipPricingTag: membershipPricingTag || '',
          membershipDiscountCode: membershipDiscountCode.trim().toUpperCase(),
          prelaunchCommittee,
          deferredPaymentChoice: method,
          ...cadencePayload
        })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data));
      }
      if (data.url) {
        window.location.href = data.url;
      } else if ((data.clientSecret || data.setupIntentClientSecret) && method === 'recurring') {
        const {
          clientSecret,
          setupIntentClientSecret,
          subscriptionId,
          paymentIntentId,
          setupIntentId,
          requiresSubscriptionFinalization,
          firstChargeBaseAmount,
          firstChargePlatformFee,
          firstChargeStripeFee,
          baseAmount,
          platformFee,
          stripeFee,
          finalAmount,
          firstChargeAmount,
          membershipYearAnchor,
          membershipDiscountAppliedMinor,
          membershipStoredCreditAppliedMinor,
          membershipStoredCreditRemainingMinor,
          learnToRowCreditAppliedMinor
        } = data;
        const checkoutParams = new URLSearchParams({
          clientSecret: clientSecret ?? '',
          setupIntentClientSecret: setupIntentClientSecret ?? '',
          subscriptionId: subscriptionId ?? '',
          paymentIntentId: paymentIntentId ?? '',
          setupIntentId: setupIntentId ?? '',
          requiresSubscriptionFinalization: String(requiresSubscriptionFinalization ?? false),
          firstChargeBaseAmount: String(firstChargeBaseAmount ?? ''),
          firstChargePlatformFee: String(firstChargePlatformFee ?? ''),
          firstChargeStripeFee: String(firstChargeStripeFee ?? ''),
          team: teamId ?? '',
          currency: currency ?? '',
          baseAmount: String(baseAmount ?? ''),
          platformFee: String(platformFee ?? ''),
          stripeFee: String(stripeFee ?? ''),
          finalAmount: String(finalAmount ?? ''),
          firstChargeAmount: String(firstChargeAmount ?? ''),
          membershipYearAnchor: membershipYearAnchor || '',
          learnToRowCreditAppliedMinor: String(learnToRowCreditAppliedMinor ?? ''),
          membershipDiscountAppliedMinor: String(membershipDiscountAppliedMinor ?? ''),
          membershipStoredCreditAppliedMinor: String(membershipStoredCreditAppliedMinor ?? ''),
          membershipStoredCreditRemainingMinor: String(membershipStoredCreditRemainingMinor ?? '')
        });
        if (data.prelaunchDeferred) checkoutParams.set('prelaunchDeferred', 'true');
        navigate(`/subscriptionCheckout?${checkoutParams.toString()}`);
      } else if (data.joined) {
        setStatus('joined');
      } else {
        throw new Error('Unexpected payment response.');
      }
    } catch (err) {
      setError(err.message || 'Payment setup failed.');
    } finally {
      setSubmitting(false);
    }
  };
  const handleApplyDiscount = async () => {
    const trimmedCode = membershipDiscountCode.trim().toUpperCase();
    if (!trimmedCode) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
      setError(null);
      return;
    }
    if (!canPay) return;
    setApplyingDiscount(true);
    setError(null);
    setDiscountMessage('');
    try {
      if (!user) throw new Error('You must be signed in to preview a discount.');
      const token = await user.getIdToken();
      const cadencePayload = effectiveCadence ? {
        selectedCadence: {
          unit: effectiveCadence.unit,
          count: effectiveCadence.count,
          discountPct: effectiveCadence.discountPct || 0,
          key: effectiveCadence.key
        }
      } : {};
      const res = await fetch(getFunctionUrl('createManualCheckoutSession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          previewOnly: true,
          teamId,
          userId: user.uid,
          memberId: user.uid,
          paymentType: 'membership',
          membershipPlanType,
          membershipPlanTierId: membershipPlanType === 'family_tier' ? selectedFamilyTier?.id || selectedFamilyTierId || null : null,
          membershipTargetUserIds,
          membershipTargetFamilyId: membershipTargetUserIds?.some(uid => uid && uid !== user.uid) ? membershipPlanType === 'individual' ? selectedIndividualTargetFamilyId || null : selectedFamilyId || null : null,
          membershipPricingTag: membershipPricingTag || '',
          membershipDiscountCode: trimmedCode,
          ...cadencePayload
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : 'Could not apply that discount code.');
      setAppliedPricingPreview({
        code: trimmedCode,
        breakdown: {
          baseAmount: Number(data?.baseAmount || 0),
          platformFee: Number(data?.platformFee || 0),
          stripeFee: Number(data?.stripeFee || 0),
          finalAmount: Number(data?.finalAmount || 0),
        },
        membershipDiscountAppliedMinor: Number(data?.membershipDiscountAppliedMinor || 0),
        membershipStoredCreditAppliedMinor: Number(data?.membershipStoredCreditAppliedMinor || 0),
        membershipStoredCreditRemainingMinor: Number(data?.membershipStoredCreditRemainingMinor || 0),
      });
      setDiscountMessage(
        Number(data?.membershipDiscountAppliedMinor || 0) > 0 || Number(data?.membershipStoredCreditAppliedMinor || 0) > 0
          ? 'Discount applied.'
          : 'Code accepted.'
      );
    } catch (err) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
      setError(err.message || 'Could not apply that discount code.');
    } finally {
      setApplyingDiscount(false);
    }
  };
  useEffect(() => {
    if (!normalizedDiscountCode) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
      return;
    }
    if (appliedPricingPreview?.code && appliedPricingPreview.code !== normalizedDiscountCode) {
      setAppliedPricingPreview(null);
      setDiscountMessage('');
    }
  }, [normalizedDiscountCode, appliedPricingPreview?.code]);
  useEffect(() => {
    setAppliedPricingPreview(null);
    setDiscountMessage('');
  }, [effectiveCadence?.key, membershipPlanType, selectedFamilyTier?.id, selectedFamilyTierId, membershipPricingTag, selectedFamilyId, selectedIndividualTargetFamilyId, membershipTargetIdsKey]);
  const handleClubManagedContinue = async () => {
    const externalShowsPrice = stripeSetup?.externalPaymentDisplayMode === 'price_only'
      || stripeSetup?.externalPaymentDisplayMode === 'price_and_instructions';
    if (!user || submitting || !hasMembershipTarget || (externalShowsPrice && cadenceMode === 'member_choice' && !choice)) return;
    setSubmitting(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, prelaunchCommittee ? 'completePrelaunchCommitteeManagedSetup' : 'completeClubManagedJoin');
      const cadencePayload = effectiveCadence ? {
        key: effectiveCadence.key,
        unit: effectiveCadence.unit,
        count: effectiveCadence.count,
      } : null;
      const result = await fn({ teamId, selectedCadence: cadencePayload });
      setPendingMemberStatus?.(result.data?.status || null);
      if (prelaunchCommittee) setStatus('joined');
      else if (result.data?.joined) setStatus('joined');
      else setStatus('waiting');
    } catch (err) {
      setError(err?.message || 'Could not submit this membership.');
    } finally {
      setSubmitting(false);
    }
  };
  if (onlineRouteSetup) return <div>
    {submitting && <LoadingModal message="Opening secure card setup…" />}
    {error && <ErrorMessage message={error} />}
    <h2 className="join-flow-section-title">Set up online renewals</h2>
    <p>Your club has approved your move to online payment. Your paid offline membership stays active until {onlineRouteSetup.pendingMembershipCollectionRouteChange.effectiveAt?.toDate?.().toLocaleDateString('en-GB') || 'your next renewal'}.</p>
    <p>Save a card for automatic payments from that renewal date. No payment is taken today. Stripe will confirm the renewal amount before you save your card.</p>
    <button type="button" className="button button-primary" disabled={!canPay} onClick={() => handlePaymentChoice('recurring')}>Continue to secure card setup</button>
  </div>;
  if (hybrid && canShowPricing && !hybridPricingPreview && !activePreview) {
    if (hybridQuoteError) return <div>
      <ErrorMessage message={hybridQuoteError} />
      <button type="button" className="button button-primary" onClick={() => setHybridQuoteRetry(value => value + 1)}>Try payment comparison again</button>
      {cadenceMode === 'member_choice' && <button type="button" className="button button-secondary" onClick={() => setChoice(null)}>Change billing frequency</button>}
    </div>;
    return <div className="text-center mt-4" role="status"><p>Loading online and offline payment totals…</p></div>;
  }
  const hybridRouteChooser = hybrid && canShowPricing ? <fieldset className="join-hybrid-options">
    <legend>Choose how to pay</legend>
    <p className="join-flow-section-copy">{prelaunchCommittee ? 'Choose online payment authorisation or arrange payment with the club. Your committee membership starts when the club goes live. No online payment is taken today.' : 'Pay securely online for immediate access, or arrange payment with the club and wait for an administrator to approve your membership.'}</p>
    <div className="join-hybrid-options__grid">
      <label className={`join-hybrid-option join-hybrid-option--recommended${membershipCollectionRoute === 'stripe' ? ' selected' : ''}`}>
        <input type="radio" name="membership-collection-route" value="stripe" checked={membershipCollectionRoute === 'stripe'} onChange={() => setMembershipCollectionRoute('stripe')} />
        <span className="join-hybrid-option__badge">Recommended</span>
        <strong>Pay online with Member Nest</strong>
        <span className="join-hybrid-option__price">{formatMinor(effectiveFinalAmount)}</span>
        <small>{prelaunchCommittee ? 'Payment taken when the club goes live' : 'Immediate access after successful payment'}</small>
        <ul>
          <li>Membership: {formatMinor(effectiveBaseAmount)}</li>
          {effectivePlatformFee > 0 && <li>Member Nest fee: {formatMinor(effectivePlatformFee)} ({stripeSetup?.platformFeeHandling === 'add' ? 'added to your total' : 'paid from club proceeds'})</li>}
          {effectiveStripeFee > 0 && <li>Stripe fee: {formatMinor(effectiveStripeFee)} ({stripeSetup?.stripeFeeHandling === 'add' ? 'added to your total' : 'paid from club proceeds'})</li>}
        </ul>
      </label>
      <label className={`join-hybrid-option${membershipCollectionRoute === 'offline' ? ' selected' : ''}`}>
        <input type="radio" name="membership-collection-route" value="offline" checked={membershipCollectionRoute === 'offline'} onChange={() => setMembershipCollectionRoute('offline')} />
        <strong>Pay the club offline</strong>
        <span className="join-hybrid-option__price">{formatMinor(offlineBaseAmount)}</span>
        <small>{prelaunchCommittee ? 'Membership starts when the club goes live' : 'Pending admin approval'}</small>
        {!prelaunchCommittee && <>
          <small>{offlineApprovalTime ? `Average approval time: ${offlineApprovalTime}` : 'Approval time varies by club'}</small>
          {offlineApprovalTime && <p>Based on recent offline confirmations. Includes time to arrange payment; your wait may vary.</p>}
        </>}
        <p>No Member Nest or Stripe transaction fee is included.</p>
      </label>
    </div>
    <p className="text-muted text-center" style={{ marginTop: '0.75rem' }}>
      {prelaunchCommittee ? 'Online payment is authorised now and collected at launch. Offline payment is arranged directly with the club.' : hybridOnlineDifference > 0
        ? `Online payment costs ${formatMinor(hybridOnlineDifference)} more and confirms access immediately after payment.`
        : 'Both routes currently have the same member total; online payment confirms access immediately.'}
    </p>
  </fieldset> : null;

  if (hybrid && canShowPricing && !membershipCollectionRoute) {
    return <>
      {error && <ErrorMessage message={error} />}
      {cadenceMode === 'member_choice' && <p className="text-sm text-muted text-center">Selected: {cadenceSummary}</p>}
      {cadenceMode === 'member_choice' && <button type="button" className="button button-secondary" onClick={() => setChoice(null)}>Change billing frequency</button>}
      {hybridRouteChooser}
    </>;
  }
  if (clubManaged || (hybrid && membershipCollectionRoute === 'offline')) {
    const gated = membershipHandling.mode === MEMBERSHIP_HANDLING_MODES.CLUB_GATED || hybrid;
    const externalDisplayMode = stripeSetup?.externalPaymentDisplayMode || 'instructions_only';
    const showPrice = externalDisplayMode === 'price_only' || externalDisplayMode === 'price_and_instructions';
    const showInstructions = externalDisplayMode === 'instructions_only' || externalDisplayMode === 'price_and_instructions';
    const instructions = typeof stripeSetup?.externalPaymentInstructions === 'string'
      ? stripeSetup.externalPaymentInstructions.trim()
      : '';
    const hasExternalPrice = showPrice && offlineBaseAmount > 0;
    const needsCadenceChoice = showPrice && cadenceMode === 'member_choice';
    const canContinue = !submitting && hasMembershipTarget && (!needsCadenceChoice || !!choice);
    const offlineYear = stripeFeeBreakdown?.membershipYear;
    const offlineProrated = offlineYear?.applied === true;
    return <>
      {error && <ErrorMessage message={error} />}
      {hybridRouteChooser}
      <div>
        <h2 className="join-flow-section-title">Club payment</h2>
        <p className="join-flow-section-copy">
          {prelaunchCommittee ? 'Arrange payment directly with the club. Your committee membership will start when the club goes live.' : gated
            ? 'This club collects membership payment outside Member Nest. Submit your membership and the club will grant access after confirming payment.'
            : 'This club collects membership payment outside Member Nest. You can join now, but you will still need to settle payment with the club.'}
        </p>
      </div>
      {needsCadenceChoice && <fieldset className="mt-4">
        <legend className="font-semibold mb-2 text-center">Choose your membership frequency</legend>
        <div className="join-cadence-options">
          {cadenceOptions.map(opt => <label key={opt.key} className={`join-cadence-option${choice?.key === opt.key ? ' selected' : ''}`}>
            <span className="join-cadence-option__content">
              <input type="radio" name="external-cadence" value={opt.key} checked={choice?.key === opt.key} onChange={() => setChoice(opt)} />
              <span className="font-medium">{formatCadenceOptionLabel(opt.label, opt.discountPct)}</span>
            </span>
          </label>)}
        </div>
        {!choice && <p className="text-xs text-center text-muted mt-2">Choose monthly or yearly to continue.</p>}
      </fieldset>}
      {hasExternalPrice && <div className="join-external-payment-detail" aria-live="polite">
        <span className="text-muted">Club-stated membership fee</span>
        <strong>{formatMinor(offlineBaseAmount)}{offlineProrated ? ' for this membership period' : effectiveCadence?.unit === 'month' ? '/month' : '/year'}</strong>
        {offlineProrated && <p className="text-muted">Prorated membership until {new Date(offlineYear.nextRenewalAt).toLocaleDateString('en-GB')}. The full renewal price is {formatMinor(offlineYear.fullCycle?.baseAmount || 0)}/year, paid to the club.</p>}
        <p className="text-muted">This amount is paid to the club outside Member Nest. No Member Nest or Stripe fee is included.</p>
      </div>}
      {showPrice && !hasExternalPrice && <p className="join-flow-section-copy">
        The club will confirm the membership amount with you directly.
      </p>}
      {showInstructions && <div className="join-external-payment-instructions">
        <h3>How to pay</h3>
        <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{renderLinkifiedText(instructions || 'The club will contact you with payment instructions.')}</p>
      </div>}
      <div className="join-payment-actions">
        <button className="button button-primary w-full" type="button" onClick={handleClubManagedContinue} disabled={!canContinue}>
          {submitting ? 'Submitting…' : gated ? 'Submit to club' : 'Join club'}
        </button>
      </div>
    </>;
  }
  if (stripeAmount === null || !stripeFeeBreakdown) {
    return <div className="text-center mt-4">
        <p>Loading payment details…</p>
      </div>;
  }
  const membershipYearInfo = stripeFeeBreakdown?.membershipYear || {};
  const membershipYearApplied = membershipYearInfo?.applied === true;
  const membershipYearAnchored = membershipYearInfo?.anchored === true;
  const fullPriceWindowActive = membershipYearInfo?.fullPriceWindowActive === true;
  const renewalFinalAmount = Number(membershipYearInfo?.fullCycle?.finalAmount || 0);
  const renewalDateLabel = membershipYearInfo?.nextRenewalAt ? new Date(membershipYearInfo.nextRenewalAt).toLocaleDateString('en-GB') : null;
  const prorationPct = membershipYearApplied ? Math.round(Number(membershipYearInfo.prorationFactor || 0) * 1000) / 10 : null;
  const planLabel = stripeFeeBreakdown?.membershipPlanLabel || (membershipPlanType === 'family_tier' ? selectedFamilyTier?.label : null) || (membershipPlanType === 'family_tier' ? 'Family Membership' : 'Individual Membership');
  const pricingOptionLabel = membershipPlanType !== 'family_tier' && membershipPricingTag ? membershipPricingTag : '';
  const coverageLabel = membershipPlanType === 'family_tier' ? `for ${membershipTargetCount} members (${planLabel})` : `for ${pricingOptionLabel || planLabel.toLowerCase()}`;
  const hasMembershipOptions = familyMembershipEnabled || [...Object.keys(stripeSetup?.tagOverrides || {}), ...Object.keys(stripeSetup?.tagOverridesYearly || {})].length > 0;
  const hasJoinFormQuestions = Array.isArray(questions) && questions.some(q => !isPricingQuestion(q));
  const hasRequiredJoinDocuments = Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0;
  const handleRestartJoinFlow = async () => {
    if (!user?.uid || !teamId) return;
    setSubmitting(true);
    setError(null);
    try {
      await setDoc(doc(db, 'teams', teamId, 'pendingMembers', user.uid), {
        status: 'invited',
        approvedAt: deleteField(),
        membershipPlanType: 'individual',
        membershipPlanTierId: deleteField(),
        membershipTargetUserIds: [user.uid],
        membershipTargetFamilyId: deleteField(),
        membershipPricingTag: '',
        membershipTargetDetails: deleteField(),
        waitlistPriority: null,
        waitlistedAt: null,
        releasedFromWaitlistAt: null,
        formResponses: deleteField(),
        documentAcceptances: deleteField(),
        appliedTags: deleteField(),
        requestedTags: deleteField(),
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
      if (hasMembershipOptions) {
        setStatus('membership_type');
      } else if (team?.availability?.joinable === 'On Approval' || hasJoinFormQuestions || hasRequiredJoinDocuments) {
        setStatus('form');
      } else {
        setStatus('payment');
      }
    } catch (err) {
      setError(err?.message || 'Could not restart the join process.');
    } finally {
      setSubmitting(false);
    }
  };
  return <>
      {submitting && <LoadingModal message="Redirecting to Stripe…" />}

      {error && <ErrorMessage message={error} />}

      <div>
        <h2 className="join-flow-section-title">Payment</h2>
        <p className="join-flow-section-copy">
          Confirm the billing frequency, apply an optional discount, then choose a payment method.
        </p>
      </div>

      {hybridRouteChooser}

      {/* Cadence selector (only when members choose) */}
      {cadenceMode === 'member_choice' && <div className="mt-4">
          <p className="font-semibold mb-2 text-center">Choose your billing frequency</p>

          <div className="join-cadence-options">
            {cadenceOptions.map(opt => <label key={opt.key} className={`join-cadence-option${choice?.key === opt.key ? ' selected' : ''}`}>
                <span className="join-cadence-option__content">
                  <input type="radio" name="cadence" value={opt.key} checked={choice?.key === opt.key} onChange={() => setChoice(opt)} />
                  <span className="font-medium">
                    {formatCadenceOptionLabel(opt.label, opt.discountPct)}
                  </span>
                </span>
              </label>)}
          </div>

          {!choice && <p className="text-xs text-center text-muted mt-2">
              Please choose monthly or yearly to continue.
            </p>}
        </div>}

      {/* Pricing + actions — only when a choice exists (or in fixed mode) */}
      {canShowPricing && <div className="mt-4 space-y-4 text-center" aria-live="polite">
          {cadenceSummary && <p className="text-sm text-muted">Selected: {cadenceSummary}</p>}

          <p className="font-bold text-lg">
            {prelaunchCommittee ? 'You’ll authorise ' : 'You’ll be charged '}
            <span className="text-primary">{formatMinor(effectiveFinalAmount)}</span>{' '}
            {prelaunchCommittee
              ? `${coverageLabel}. Nothing is charged until the club's first launch attempt.`
              : membershipYearApplied ? `today (prorated) ${coverageLabel}.` : `${coverageLabel}.`}
          </p>
          {membershipYearAnchored && <p className="text-sm text-muted">
              {membershipYearApplied
                ? `Proration: ${prorationPct}% of the yearly cycle.`
                : fullPriceWindowActive
                  ? 'The full annual price applies during the club’s post-renewal window.'
                  : 'The full annual price applies.'}
              {renewalDateLabel && renewalFinalAmount > 0 ? ` Renews on ${renewalDateLabel} at ${formatMinor(renewalFinalAmount)}.` : ''}
            </p>}

          <ul className="text-sm text-left text-muted space-y-1 max-w-md mx-auto">
            <li>
              <strong>{formatMinor(effectiveBaseAmount)}</strong>{' '}
              {membershipYearApplied ? 'prorated base subscription' : 'base subscription'}
            </li>
            {effectivePlatformFee > 0 && <li>
                <strong>{formatMinor(effectivePlatformFee)}</strong> Member Nest platform fee
              </li>}
            {effectiveStripeFee > 0 && <li>
                <strong>{formatMinor(effectiveStripeFee)}</strong> Stripe fees
              </li>}
            {effectiveDiscountAppliedMinor > 0 && <li>
                <strong>-{formatMinor(effectiveDiscountAppliedMinor)}</strong> membership discount
              </li>}
            {effectiveStoredCreditAppliedMinor > 0 && <li>
                <strong>-{formatMinor(effectiveStoredCreditAppliedMinor)}</strong> stored membership credit
              </li>}
          </ul>

          <div className="max-w-md mx-auto text-left">
            <label className="form-group" style={{ marginBottom: 0 }}>
              <span className="form-label">Discount code</span>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                <input
                  className="form-input"
                  value={membershipDiscountCode}
                  onChange={(e) => setMembershipDiscountCode(e.target.value.toUpperCase())}
                  placeholder="Optional"
                  disabled={submitting || applyingDiscount}
                />
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={handleApplyDiscount}
                  disabled={submitting || applyingDiscount || !membershipDiscountCode.trim()}
                >
                  {applyingDiscount ? 'Applying…' : 'Apply'}
                </button>
              </div>
            </label>
            <p className="text-xs text-muted" style={{ marginTop: '0.35rem' }}>
              If a code reduces today&apos;s manual payment to zero, membership will activate immediately without Stripe. Recurring memberships may still ask for a card so future renewals can be charged.
            </p>
            {discountMessage ? <p className="text-xs text-muted" style={{ marginTop: '0.35rem' }}>{discountMessage}</p> : null}
            {activePreview && effectiveStoredCreditRemainingMinor > 0 ? (
              <p className="text-xs text-muted" style={{ marginTop: '0.35rem' }}>
                Stored membership credit remaining after today: {formatMinor(effectiveStoredCreditRemainingMinor)}.
              </p>
            ) : null}
          </div>

          <div className="join-payment-actions">
            {hasMembershipOptions && pendingMemberStatus !== 'approved' && <button className="button button-secondary w-full" onClick={() => setStatus('membership_type')} disabled={submitting}>
                Change membership type
              </button>}

            <button className="button button-primary w-full" onClick={() => handlePaymentChoice('recurring')} disabled={!canPay} aria-disabled={!canPay} title={!canPay ? 'Choose monthly or yearly first' : undefined}>
              {prelaunchCommittee ? 'Authorise recurring subscription' : 'Set up recurring subscription'}
            </button>

            <button className="button button-primary w-full" onClick={() => handlePaymentChoice('manual')} disabled={!canPay} aria-disabled={!canPay} title={!canPay ? 'Choose monthly or yearly first' : undefined}>
              {prelaunchCommittee ? 'Authorise one-off payment' : 'Pay manually'}
            </button>
          </div>

          {pendingMemberStatus === 'approved' && <div className="pt-1">
              <br></br><p className="text-xs text-muted" style={{
            marginBottom: '0.4rem'
          }}>
                Need to review updated requirements or details?
              </p>
              <button className="btn btn--secondary btn--sm" onClick={handleRestartJoinFlow} disabled={submitting}>
                Restart join
              </button>
            </div>}
        </div>}
    </>;
}
